import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, FlatList, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import type { ColorTheme } from '../../src/utils/theme';

interface LocationOption { id: string; name: string; city: string; state: string; address: string; }
interface BayOption { id: string; name: string; type: string; isActive: boolean; controllerId: string | null; pricePerMinute: number | null; tiers: any[] | null; presets: number[] | null; }

/** AdminScanScreen - start complimentary washes by selecting location and bay. */
export default function AdminScanScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { ownerId } = useLocalSearchParams<{ ownerId?: string }>();
  const [manualUrl, setManualUrl] = useState('');
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationOption[]>([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [bays, setBays] = useState<BayOption[]>([]);
  const [baysLoading, setBaysLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const url = ownerId
          ? `/api/admin/locations?limit=500&ownerId=${ownerId}`
          : '/api/admin/locations?limit=500';
        const data = await api<{ locations: LocationOption[] }>(url);
        setLocations(data.locations);
        setFilteredLocations(data.locations);
      } catch (err) { console.error('load locations:', err); }
      finally { setLocationsLoading(false); }
    };
    loadLocations();
  }, [ownerId]);

  useEffect(() => {
    if (!locationSearch.trim()) {
      setFilteredLocations(locations);
    } else {
      const q = locationSearch.toLowerCase();
      setFilteredLocations(locations.filter(l =>
        l.name.toLowerCase().includes(q) || l.city.toLowerCase().includes(q) || l.address.toLowerCase().includes(q)
      ));
    }
  }, [locationSearch, locations]);

  const selectLocation = async (loc: LocationOption) => {
    setSelectedLocation(loc);
    setBaysLoading(true);
    try {
      const data = await api<{ bays: BayOption[] }>(`/api/admin/locations/${loc.id}/bays`);
      setBays(data.bays.filter(b => b.isActive));
    } catch { setBays([]); }
    finally { setBaysLoading(false); }
  };

  const selectBay = (bay: BayOption) => {
    router.push(`/wash/${selectedLocation!.id}/${bay.id}` as never);
  };

  const handleManualEntry = () => {
    const match = manualUrl.match(/wash\/([^/\s]+)\/([^/\s]+)/);
    if (match) {
      router.push(`/wash/${match[1]}/${match[2]}` as never);
    } else {
      toast.show({ type: 'error', title: 'Invalid URL', message: 'Please enter a valid RinsePay wash URL' });
    }
  };

  const goBack = () => {
    setSelectedLocation(null);
    setBays([]);
  };

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <Text style={styles.pageTitle}>Admin Wash Scan</Text>
      <Text style={styles.pageSubtitle}>{ownerId ? "Showing this owner's locations — select a bay to start a wash" : 'Select a location and bay to start a wash, or enter a URL manually'}</Text>

      {!selectedLocation ? (
        <>
          {/* Location Search */}
          <View style={styles.searchRow}>
            <Ionicons name="search" size={18} color={colors.textLight} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search locations by name, city, or address..."
              placeholderTextColor={colors.textLight}
              value={locationSearch}
              onChangeText={setLocationSearch}
              autoCapitalize="none"
              maxLength={100}
            />
            {locationSearch.length > 0 && (
              <TouchableOpacity onPress={() => setLocationSearch('')}>
                <Ionicons name="close-circle" size={18} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </View>

          {locationsLoading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
          ) : (
            <FlatList ListFooterComponent={<PageFooter />}
              data={filteredLocations}
              keyExtractor={item => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity style={styles.locRow} onPress={() => selectLocation(item)}>
                  <View style={styles.locIcon}>
                    <Ionicons name="location" size={20} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.locName}>{item.name}</Text>
                    <Text style={styles.locAddr}>{item.address}, {item.city}, {item.state}</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
                </TouchableOpacity>
              )}
              ListEmptyComponent={<Text style={styles.empty}>No locations found</Text>}
            />
          )}
        </>
      ) : (
        <>
          {/* Bay Selection */}
          <TouchableOpacity style={styles.backRow} onPress={goBack}>
            <Ionicons name="arrow-back" size={18} color={colors.primary} />
            <Text style={styles.backText}>Back to locations</Text>
          </TouchableOpacity>

          <View style={styles.selectedHeader}>
            <Ionicons name="location" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <Text style={styles.selectedName}>{selectedLocation.name}</Text>
              <Text style={styles.selectedAddr}>{selectedLocation.address}, {selectedLocation.city}, {selectedLocation.state}</Text>
            </View>
          </View>

          <Text style={styles.sectionLabel}>Select a Bay</Text>

          {baysLoading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.md }} />
          ) : bays.length === 0 ? (
            <Text style={styles.empty}>No active bays at this location</Text>
          ) : (
            <FlatList ListFooterComponent={<PageFooter />}
              data={bays}
              keyExtractor={item => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity style={styles.bayCard} onPress={() => selectBay(item)}>
                  <View style={styles.bayIconWrap}>
                    <Ionicons name={item.type === 'automatic' ? 'car' : 'water'} size={24} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.bayCardName}>{item.name}</Text>
                    <Text style={styles.bayCardMeta}>
                      {item.type === 'self-serve' ? 'Self-Serve' : 'Automatic'}
                      {item.pricePerMinute ? ` · $${item.pricePerMinute.toFixed(2)}/min` : ''}
                      {item.tiers && item.tiers.length > 0 ? ` · ${item.tiers.length} tiers` : ''}
                      {item.presets && item.presets.length > 0 ? ` · ${item.presets.length} presets` : ''}
                    </Text>
                    {item.controllerId && <Text style={styles.bayCardCtrl}>Controller: {item.controllerId}</Text>}
                  </View>
                  <Ionicons name="play-circle" size={28} color={colors.primary} />
                </TouchableOpacity>
              )}
            />
          )}
        </>
      )}

      {/* Manual URL Entry */}
      <View style={styles.divider}>
        <View style={styles.line} />
        <Text style={styles.dividerText}>or enter URL manually</Text>
        <View style={styles.line} />
      </View>
      <View style={styles.manualEntry}>
        <TextInput
          style={styles.input}
          placeholder="Paste wash URL or code"
          placeholderTextColor={colors.textLight}
          value={manualUrl}
          onChangeText={setManualUrl}
          autoCapitalize="none"
          maxLength={255}
        />
        <TouchableOpacity style={styles.goBtn} onPress={handleManualEntry}>
          <Ionicons name="arrow-forward" size={20} color={colors.white} />
        </TouchableOpacity>
      </View>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  pageSubtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  searchRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: c.surface, borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderWidth: 1, borderColor: c.border, marginBottom: spacing.sm,
  },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text },
  locRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  locIcon: { width: rs(40), height: rs(40), borderRadius: rs(20), backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center' },
  locName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  locAddr: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  backRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.sm },
  backText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  selectedHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.primary + '30',
  },
  selectedName: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  selectedAddr: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  sectionLabel: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  bayCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  bayIconWrap: { width: rs(48), height: rs(48), borderRadius: borderRadius.md, backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center' },
  bayCardName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  bayCardMeta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  bayCardCtrl: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.sm },
  divider: { flexDirection: 'row', alignItems: 'center', marginVertical: spacing.md },
  line: { flex: 1, height: 1, backgroundColor: c.border },
  dividerText: { marginHorizontal: spacing.md, fontSize: fontSize.sm, color: c.textLight },
  manualEntry: { flexDirection: 'row', gap: spacing.sm },
  input: {
    flex: 1, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
  },
  goBtn: {
    width: rs(44), height: rs(44), borderRadius: borderRadius.md,
    backgroundColor: c.primary, justifyContent: 'center', alignItems: 'center',
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
